import Compressor from 'compressorjs'
import CanvasCompress from 'canvas-compress'
import { isEmpty, forIn, cloneDeep, isObject, isArray } from 'lodash'
import { uploadImg } from '@/api/system/imageGroup'
import { getCityTree, getCityVersion as getCityVersionApi } from '@/api/common'
import dayjs from 'dayjs'
import store from '@/store'
import Clipboard from 'clipboard/dist/clipboard.min.js'
import { nanoid } from 'nanoid'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import JSZip from 'jszip'
import * as mammoth from 'mammoth'

// 深度拷贝对象
export function deepClone(source) {
  if (!source && typeof source !== 'object') {
    throw new Error('error arguments', 'shallowClone')
  }
  const targetObj = source.constructor === Array ? [] : {}
  Object.keys(source).forEach((keys) => {
    if (source[keys] && typeof source[keys] === 'object') {
      targetObj[keys] = deepClone(source[keys])
    } else {
      targetObj[keys] = source[keys]
    }
  })
  return targetObj
}

// 获得字符串字符长度，中文2，英文1
export function getStrLength(str) {
  let realLength = 0
  let len = str.length
  let charCode = -1
  for (let i = 0; i < len; i++) {
    charCode = str.charCodeAt(i)
    if (charCode >= 0 && charCode <= 128) realLength += 1
    else realLength += 2
  }
  return realLength
}

// 去除字符串左右两边空格
export function trimLR(str) {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

//判断是否为空
export function isNull(o) {
  return o == undefined || o == 'undefined' || o == null || o == '' || o == 'null' || o == [] || o == 0
}

export function isExternal(path) {
  return /^(https?:|mailto:|tel:)/.test(path)
}
/**
 * 压缩图片
 * @param {File} file 传入的图片文件
 * @param {Number} rate 压缩比率，0-1
 * @param {Number} limitSize 最大大小，单位B，超出时使用canvas压缩
 * @param {Boolean} forceCanvasCompress 是否强制启用canvas压缩
 * @return {File} 压缩后的文件
 */
export function compressImage(file, rate, limitSize, forceCanvasCompress = false) {
  return new Promise(function (resolve, reject) {
    if (!file) {
      return reject('文件不存在')
    }
    if (!rate || rate == 1 || file.type == 'image/gif') {
      return resolve(file)
    }
    //  暂时关闭canvas压缩
    // if (forceCanvasCompress) {
    //   // 强制使用canvas压缩
    //   canvasCompress(file, rate).then(resolve).catch(reject)
    //   return
    // }
    new Compressor(file, {
      quality: rate,
      success(result) {
        let resFile = blobToFile(result)
        //  暂时关闭canvas压缩
        // if (limitSize && resFile.size > limitSize) {
        //   // 压缩仍过大使用canvas压缩
        //   canvasCompress(file, rate).then(resolve).catch(reject)
        // } else {
        resolve(resFile)
        // }
      },
      error(err) {
        reject(err.message)
      },
    })
  })
}
/**
 * canvas压缩图片
 * @param {File} file 传入的图片文件
 * @param {Number} rate 压缩比率，0-1
 * @return {File} 压缩后的文件
 */
export function canvasCompress(file, rate) {
  return new Promise((resolve, reject) => {
    if (!file) {
      return reject('文件不存在')
    }
    if (!rate) {
      return resolve(file)
    }
    const compressor = new CanvasCompress({
      type: CanvasCompress.MIME.JPEG,
      width: 9999,
      height: 9999,
      quality: rate,
    })
    compressor
      .process(file)
      .then(({ source, result }) => {
        const { blob: sourceBlob } = source
        const { blob } = result
        blob.name = sourceBlob.name
        resolve(blobToFile(blob))
      })
      .catch(reject)
  })
}
/**
 * @description 获取树形结构中最底层集合
 * @param {Array} list 树形数据
 * @param {String} childrenProp 子级属性名
 * @returns {Array} 最底层集合
 */
export function getTreeChildrenList(list, childrenProp) {
  childrenProp = childrenProp || 'children'
  let childrenList = []
  list.forEach((item) => {
    if (!isNull(item[childrenProp])) {
      childrenList = childrenList.concat(getTreeChildrenList(item[childrenProp], childrenProp))
    } else {
      childrenList.push(item)
    }
  })
  return childrenList
}
/**
 * 查找树形结构中的指定元素并返回嵌套路径元素
 * @param {Array} dataArr 需要查找的目标数组
 * @param {String} childrenProp 子级属性名
 * @param {Object} targetObj 需要满足条件的属性集合
 * @param {Array|Boolean} resArr 查找到的嵌套路径数组（每个值为一个对象），未找到返回false
 * @return {Array|Boolean} 查找到的嵌套路径数组（每个值为一个对象），未找到返回false
 */
export function findItemInArr(dataArr, childrenProp, targetObj, resArr) {
  if (isEmpty(targetObj)) {
    return false
  }
  childrenProp = childrenProp || 'children'
  resArr = resArr || []
  for (let i = 0, len = dataArr.length; i < len; i++) {
    let isMatch = true
    forIn(targetObj, (item, key) => {
      if (!dataArr[i][key] || dataArr[i][key] != item) {
        isMatch = false
        return false
      }
    })
    if (isMatch) {
      resArr.push({ __index: i, ...dataArr[i] })
      return resArr
    } else {
      resArr.push({ __index: i, ...dataArr[i] })
    }
    let children = dataArr[i][childrenProp]
    if (!isNull(children)) {
      let result = findItemInArr(children, childrenProp, targetObj, resArr)
      if (result) {
        return resArr
      }
    }
    resArr.pop()
  }
  return false
}
/**
 * blob转文件
 * @param {Blob} blob 传入的blob数据
 * @return {File} 转换后的文件
 */
export function blobToFile(blob) {
  const blobType = blob.type
  return new File([blob], blob.name, {
    type: blobType,
  })
}
/**
 * 顺序执行Promise队列
 * @param {Array} promiseArr 待执行的Promise数组
 * @returns {Promise}  每个Promise的执行结果形成的数组
 */
export function reducePromise(promiseArr) {
  let sequence = Promise.resolve()
  let resArr = []
  promiseArr.forEach((item) => {
    sequence = sequence.then(item).then((data) => {
      resArr.push(data)
      return resArr
    })
  })
  return sequence
}
/**
 * 链接图片转存至图片库
 * @param {String} url 图片链接，必传
 * @param {String} imgName 图片名称（带后缀），可选
 * @param {Number} rate 压缩比率，可选，范围0-1，默认为1
 * @returns {Promise}  返回图片库CDN链接
 */
export function uploadUrlImg(url, imgName, rate = 1) {
  return new Promise((resolve) => {
    if (isNull(url)) {
      resolve('')
      return
    }
    if (!imgName) {
      url = url.replace(/\?.*$/, '') // 去除链接后缀中的防缓存内容
    }
    imgName = imgName || url.substring(url.lastIndexOf('/') + 1).toLowerCase()
    getBase64Img(url)
      .then((dataURL) => {
        let file = dataURLtoFile(dataURL, imgName)
        compressImage(file, rate).then((resFile) => {
          const body = {
            file: resFile,
            group_id: '',
            upload: true,
          }
          uploadImg(body).then((res) => {
            if (res.code === 0) {
              resolve(res.data)
            } else {
              resolve('')
            }
          })
        })
      })
      .catch(() => {
        resolve('')
      })
  })
}

// 将图片转为base64编码，返回Promise
export function getBase64Img(imgUrl, crossOriginWithCredentials) {
  //获取扩展名
  var canvas = document.createElement('canvas')
  var ctx = canvas.getContext('2d')
  var img = null
  var ext = null
  return new Promise((resolve, reject) => {
    if (isNull(imgUrl)) {
      reject('请传递图片地址')
      return
    }
    ext = imgUrl.lastIndexOf('.') > -1 ? imgUrl.substring(imgUrl.lastIndexOf('.') + 1).toLowerCase() : 'jpeg'
    ext = ext.split('?')[0].replace('jpg', 'jpeg')
    img = new Image()
    img.onload = function () {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0, img.width, img.height)
      var dataURL = canvas.toDataURL('image/' + ext)
      resolve(dataURL)
      canvas = null
    }
    img.onerror = function () {
      reject('图片加载失败')
    }
    img.setAttribute('crossOrigin', crossOriginWithCredentials ? 'use-credentials' : 'Anonymous')
    // 图片增加跨域头后需要带上时间戳参数否则会加载失败
    var curTime = new Date().getTime()
    var suffix = imgUrl.indexOf('?') > -1 ? '&time=' + curTime : '?time=' + curTime
    img.src = imgUrl + suffix
  })
}

//将base64转换为文件
export function dataURLtoFile(dataurl, filename) {
  var arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}
// 自动调节文本域高度
export function autoHeight(el) {
  el.style.height = 'auto'
  // el.scrollTop = 0 //防抖动
  var _scrollHeight = el.scrollHeight
  el.style.height = _scrollHeight + 'px'
}

/**
 * 阿拉伯数字转中文
 * @param {Number} num 传入的阿拉伯数字
 * @returns {String} 中文数字
 */
export function toChineseNum(num) {
  const chineseNumArr = '零一二三四五六七八九'.split('')
  return chineseNumArr[num] || ''
}
/**
 * 判断文字是否溢出
 * @param {Element} el 待判断元素
 * @returns {Boolean} 是否溢出
 */
export function checkOverflow(el) {
  const curOverflow = el.style.overflow

  if (!curOverflow || curOverflow === 'visible') {
    el.style.overflow = 'hidden'
  }
  const isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight

  el.style.overflow = curOverflow
  return isOverflowing
}
/**
 * 判断图片全部加载完成
 * @param {Element} imgArr 待判断图片元素集合
 * @returns {Promise}
 */
export function imgAllLoaded(imgArr) {
  let imgLoadArr = []
  Array.prototype.forEach.call(imgArr, (img) => {
    if (img.tagName != 'IMG') {
      return
    }
    imgLoadArr.push(
      new Promise((resolve) => {
        // 若已加载完成则直接resolve否则监听到加载完成再resolve
        if (isNull(img.getAttribute('src'))) {
          resolve()
        } else if (img.complete) {
          resolve()
        } else {
          // 加载成功或失败均resolve
          img.onload = () => {
            resolve()
          }
          img.onerror = () => {
            resolve()
          }
        }
      })
    )
  })
  // 手动catch，允许出现异常
  return Promise.all(imgLoadArr).catch(() => {})
}
/**
 * 复制DOM格式的html代码至剪贴板
 * @param {String} content
 * @returns {Promise}
 */
export function copyDOM(content) {
  return new Promise((resolve, reject) => {
    let tempDiv = document.createElement('div')
    Object.assign(tempDiv.style, {
      width: '375px',
      position: 'fixed',
      pointerEvents: 'none',
      opacity: 0,
      top: 0,
      left: 0,
    })
    tempDiv.innerHTML = content
    document.body.appendChild(tempDiv)
    // 所有图片加载完成后执行高度判断逻辑防止判断失误
    imgAllLoaded(tempDiv.querySelectorAll('img'))
      .then(() => {
        if (
          tempDiv.querySelector('.max-height-wrapper-block') &&
          tempDiv.querySelectorAll('.max-height-only-block') &&
          !checkOverflow(tempDiv.querySelector('.max-height-wrapper-block'))
        ) {
          // 若存在仅在高度溢出才显示的区块，则处理高度不溢出时的逻辑
          Array.prototype.forEach.call(tempDiv.querySelectorAll('.max-height-only-block'), (el) => {
            el.style.display = 'none'
          })
        }
        // 选中待复制区块
        let selection = window.getSelection()
        selection.removeAllRanges()
        let rangeObj = document.createRange()
        rangeObj.selectNode(tempDiv)
        selection.addRange(rangeObj)
        // 执行复制并删除临时DOM
        document.execCommand('copy')
        selection.removeAllRanges()
        tempDiv.remove()
        resolve()
      })
      .catch(reject)
  })
}
// 获取实际子串
export function realSubString(str, start, length) {
  str = str || ''
  start = start || 0
  length = length || 0
  return Array.from(str).slice(start, length).join('')
}
// 字符串实际长度
export function realLength(str) {
  str = str || ''
  return Array.from(str).length
}
// 转换换行符
export function rnToBr(str) {
  str = str || ''
  str = str.replace(/(\r\n)|(\r)|(\n)/g, '<br/>')
  return str
}
// 反向转换换行符
export function brToRn(str) {
  str = str || ''
  str = str.replace(/<br.*?>|<br.*?\/>/g, '\n')
  return str
}
//强制下载文件
var URL = window.URL || window.webkitURL
export function saveAs(blob, filename) {
  var type = blob.type
  var force_saveable_type = 'application/octet-stream'
  if (type && type != force_saveable_type) {
    // 强制下载，而非在浏览器中打开
    var slice = blob.slice || blob.webkitSlice || blob.mozSlice
    blob = slice.call(blob, 0, blob.size, force_saveable_type)
  }
  var url = URL.createObjectURL(blob)
  var save_link = document.createElementNS('http://www.w3.org/1999/xhtml', 'a')
  save_link.href = url
  save_link.download = filename

  var event = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
  })
  save_link.dispatchEvent(event)
  URL.revokeObjectURL(url)
}

/**
 * @description 下载pdf
 * @param {String} url pdf链接
 * @param {String} filename 下载的文件名
 */
export function downloadPdf(url, filename) {
  var oReq = new XMLHttpRequest()
  var URLToPDF = url
  oReq.open('GET', URLToPDF, true)
  oReq.responseType = 'blob'
  oReq.onload = function () {
    var file = new Blob([oReq.response], {
      type: 'application/pdf',
    })
    saveAs(file, filename)
  }
  oReq.send()
}
/**
 * 获取图片格式
 * @param {String} imgUrl 图片地址，支持base64
 * @returns {String} 图片格式，如jpg，png，gif
 */
export function getImageType(imgUrl) {
  let resType = ''
  if (imgUrl.indexOf('data:image') == 0) {
    // base64数据
    resType = imgUrl.split(/:|\/|;/)[2]
  } else {
    // url链接
    imgUrl = imgUrl.split('?')[0]
    resType = imgUrl.substring(imgUrl.lastIndexOf('.') + 1)
  }
  resType = resType.toLowerCase().replace('jpeg', 'jpg')
  return resType
}
/**
 * 深度对比两对象是否有差异
 * @param {Object} oldItem 原对象
 * @param {Object} newItem 待比较对象
 * @param {Array} arrayKey 类型为数组的key
 * @param {Array} objArrayKey 类型为对象数组的key
 * @param {Array} objKey 类型为对象的key
 * @returns {Boolean} 是否有差异
 */
export function checkObjDiff(oldItem = {}, newItem = {}, arrayKey = [], objArrayKey = [], objKey = []) {
  let changed = false
  for (let key in newItem) {
    if (changed) {
      break
    }
    if (arrayKey.indexOf(key) > -1) {
      const newValue = cloneDeep(newItem[key])
      const oldValue = cloneDeep(oldItem[key])
      changed = newValue.sort().join() !== oldValue.sort().join()
    } else if (objArrayKey.indexOf(key) > -1) {
      if (newItem[key].length !== oldItem[key].length) {
        changed = true
      } else {
        newItem[key].some((item, i) => {
          for (let objKey in item) {
            if (changed) {
              break
            }
            if (isArray(item[objKey])) {
              item[objKey].some((child, j) => {
                if (changed) {
                  return true
                }
                changed = checkObjDiff(oldItem[key][i][objKey][j], child, arrayKey, objArrayKey, objKey)
              })
            } else if (isObject(item[objKey])) {
              changed = checkObjDiff(oldItem[key][i][objKey], item[objKey], arrayKey, objArrayKey, objKey)
            } else {
              changed = item[objKey] !== oldItem[key][i][objKey]
            }
          }
          if (changed) {
            return true
          }
        })
      }
    } else if (objKey.indexOf(key) > -1) {
      for (let objKey in newItem[key]) {
        if (changed) {
          break
        }
        if (isArray(newItem[key][objKey])) {
          newItem[key][objKey].some((child, j) => {
            if (changed) {
              return true
            }
            changed = checkObjDiff(oldItem[key][objKey][j], child, arrayKey, objArrayKey, objKey)
          })
        } else if (isObject(newItem[key][objKey])) {
          changed = checkObjDiff(oldItem[key][objKey], newItem[key][objKey], arrayKey, objArrayKey, objKey)
        } else {
          changed = newItem[key][objKey] !== oldItem[key][objKey]
        }
      }
    } else {
      changed = newItem[key] !== oldItem[key]
    }
  }
  return changed
}

/**
 * 动态加载css/js文件
 * @param {Array} fileArr 文件地址数组
 * @returns {Promise}
 */
const loadedMap = {} //标记是否完成
const loadedCallbackArrMap = {} //完成后的回调数组
export function loadFile(fileArr) {
  return new Promise(function (resolve, reject) {
    if (Object.prototype.toString.call(fileArr) !== '[object Array]') {
      fileArr = [fileArr]
    }
    var promiseArr = []
    fileArr.forEach(function (item) {
      promiseArr.push(
        new Promise(function (_resolve, _reject) {
          var fileType = item.split('?')[0].substring(item.lastIndexOf('.') + 1)
          if (fileType == 'js') {
            if (!document.querySelector('script[src^="' + item + '"]')) {
              var script = document.createElement('script')
              script.onload = function () {
                loadedMap[item] = true
                if (!isNull(loadedCallbackArrMap[item])) {
                  // 执行其他回调
                  loadedCallbackArrMap[item].forEach((fn) => {
                    typeof fn === 'function' && fn()
                  })
                  delete loadedCallbackArrMap[item]
                }
                _resolve()
              }
              script.onerror = function () {
                _reject()
              }
              script.type = 'text/javascript'
              script.src = item
              document.body.appendChild(script)
            } else {
              if (loadedMap[item]) {
                // 已经加载完成
                _resolve()
              } else {
                // 未加载完成推入回调数组
                loadedCallbackArrMap[item] = loadedCallbackArrMap[item] || []
                loadedCallbackArrMap[item].push(_resolve)
              }
            }
          } else if (fileType == 'css') {
            if (!document.querySelector('link[href^="' + item + '"]')) {
              var link = document.createElement('link')
              link.onload = function () {
                loadedMap[item] = true
                if (!isNull(loadedCallbackArrMap[item])) {
                  // 执行其他回调
                  loadedCallbackArrMap[item].forEach((fn) => {
                    typeof fn === 'function' && fn()
                  })
                  delete loadedCallbackArrMap[item]
                }
                _resolve()
              }
              link.onerror = function () {
                _reject()
              }
              link.rel = 'stylesheet'
              link.href = item
              document.body.appendChild(link)
            } else {
              if (loadedMap[item]) {
                // 已经加载完成
                _resolve()
              } else {
                // 未加载完成推入回调数组
                loadedCallbackArrMap[item] = loadedCallbackArrMap[item] || []
                loadedCallbackArrMap[item].push(_resolve)
              }
            }
          }
        })
      )
    })
    Promise.all(promiseArr).then(resolve).catch(reject)
  })
}
/**
 * 网络图片转file
 * @param {String} url 图片地址
 * @returns {Promise} 转换完成的文件
 */
export function cdnToFile(url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    //这里可以是图片、音频、视频、文本文件等等前端可以选择的文件
    const nowTime = new Date().getTime() //获取当前时间作为随机数
    xhr.open('get', url + '?time=' + nowTime, true)
    xhr.responseType = 'blob'
    xhr.onload = function () {
      if (this.status == 200) {
        let blob = this.response //此对象等同于前端人工选择文件载入的内容
        let file = new window.File([blob], 'test.' + blob.type.split('/')[1], {
          type: blob.type,
        })
        resolve(file)
      }
    }
    xhr.onerror = (e) => {
      reject(e)
    }
    xhr.send()
  })
}

/**
 * @description 对html文本进行标点符号替换
 * @param {String} str 待替换的字符串
 * @return {String} 替换完成的字符串
 */
export function doHtmlSymbolReplace(str) {
  let rst
  str = str || ''
  let total_ret = []
  let reg = /<\/?[a-z!][a-z0-9-]*[^>]*>/gi
  while ((rst = reg.exec(str))) {
    total_ret.push(rst)
  }
  if (total_ret.length > 0) {
    let str_arr = str.split(reg)
    let merge = []
    for (let i in str_arr) {
      merge.push({ content: str_arr[i], is_tag: 0 })
      merge.push({ content: total_ret[i] ? total_ret[i][0] : '', is_tag: 1 })
    }
    for (let i in merge) {
      if (merge[i]['is_tag'] == 0) {
        merge[i]['content'] = replaceSymbols(merge[i]['content'])
      }
    }
    str = arrayColumn(merge, 'content').join('')
  } else {
    str = replaceSymbols(str)
  }
  return str
}

/**
 * @description 对字符串中的符号进行按需替换
 * @param {String} str 需要替换的字符串
 * @return {String} 替换完成的字符串
 */
function replaceSymbols(str) {
  str = str || ''
  str = str.replace(/;/gi, '；') //替换分号， 排除&nbsp; &#39;的干扰
  str = str.replace(/(&[a-z]{2}|&[a-z]{3}|&[a-z]{4}|&[a-z]{5}|&[a-z]{6}|&#\d{2}|&#\d{3})(；)/gi, '$1' + ';')
  str = str.replace(/(\d|\d\s)(X|x)(\s\d|\d)/gi, '$1' + '×' + '$3') // 数字间的乘号：X→×（如3X4，  3 X4）
  const from = [',', ':', '(', ')', '＋', '％', '－', '／', '．', '〜', '~']
  const to = ['，', '：', '（', '）', '+', '%', '-', '/', '.', '～', '～']
  for (let i in from) {
    str = str.replaceAll(from[i], to[i])
  }
  return str
}

//从二维数组中获取键值对类似于PHP的array_column
function arrayColumn(obj, value, key) {
  value = value || ''
  key = key || ''
  let ret
  if (key == '' && value == '') return false
  if (key != '') {
    ret = {}
    for (let i in obj) {
      if (value == '') {
        ret[obj[i][key]] = obj[i]
      } else {
        ret[obj[i][key]] = obj[i][value]
      }
    }
  } else if (key == '') {
    ret = []
    for (let i in obj) {
      ret.push(obj[i][value])
    }
  }
  return ret
}
/**
 * 获取排序差异
 * @param {Array} originList 排序前的数组
 * @param {Array} curList 排序后的数组
 * @returns {Array} 差异数组
 */
export function getSortChangeArr(originList, curList) {
  let endIndex = curList.length - 1
  for (let i = curList.length - 1; i >= 0; i--) {
    if (curList[i].id !== originList[i].id) {
      endIndex = i
      break
    }
  }
  let lastSort = 0
  if (endIndex < curList.length - 1) {
    lastSort = parseInt(curList[endIndex + 1].sort)
  }
  const sortedList = curList.filter((v, i) => i <= endIndex).reverse()
  let changedList = []
  sortedList.forEach((item, i) => {
    changedList.push({ id: item.id, sort: lastSort + i + 1 })
  })
  return changedList
}

/**
 * 递归所有节点执行一定操作
 * @param {Array} data 源数据
 * @param {Function} callback 回调方法
 * @param {String} childName 子级字段，默认为children
 * @param {Object} extraParams 额外需要带入递归的参数
 * @param {Object} extraConfig 额外配置
 */
export function loopNode(data, callback, childName = 'children', extraParams, extraConfig) {
  extraParams = extraParams || {}
  extraConfig = extraConfig || {}
  data.forEach((item, index, arr) => {
    typeof callback === 'function' && callback(item, index, arr, extraParams)
    const titleName = extraParams.titleName || 'title'
    if (item[childName] && item[childName].length > 0) {
      loopNode(
        item[childName],
        callback,
        childName,
        {
          ...extraParams,
          titleName,
          full_title: [extraParams.full_title, item[titleName]].filter(Boolean).join(' / '),
          pid: [extraParams.pid, item.id].filter(Boolean).join(extraConfig.pidSplit || '_'),
        },
        extraConfig
      )
    }
  })
}
/**
 * 递归寻找节点（返回目标节点）
 * @param {Array} data 源数据
 * @param {*} key 目标属性值
 * @param {Function} callback 回调方法
 * @param {String} keyName 目标属性字段，默认为key
 * @param {String} childName 子级字段，默认为children
 * @returns {*} 目标节点
 */
export function findNode(data, key, callback, keyName = 'key', childName = 'children') {
  let foundEl = null
  data.some((item, index, arr) => {
    if (item[keyName] == key) {
      typeof callback === 'function' && callback(item, index, arr)
      foundEl = item
      return true
    }
    if (item[childName] && item[childName].length > 0) {
      let _foundEl = findNode(item[childName], key, callback, keyName, childName)
      if (_foundEl) {
        foundEl = _foundEl
        return true
      }
    }
  })
  return foundEl
}
/**
 * 获取人性化时间
 * @param {Date} date 指定需要转换的目标时间
 * @param {String} formatStr 指定最终全日期需要转换的格式
 * @param {Boolean} forceFullTime 是否强制全日期
 * @param {Boolean} checkSameYear 是否检查年份相同并省略
 * @returns {String} 人性化时间
 */
export function getHumanizedTime(targetDate, formatStr = 'YYYY-MM-DD HH:mm', forceFullTime = false, checkSameYear = false) {
  let resText = ''
  if (/^\d+$/.test(targetDate + '')) {
    // 纯数字的情况下，认为是时间戳，需要转换为毫秒级时间戳
    targetDate = parseInt((targetDate + '').padEnd(13, 0))
    targetDate = new Date(targetDate)
  } else if (typeof targetDate === 'string') {
    targetDate = targetDate.replace(/-/g, '/') //防止ios端解析出错
    targetDate = new Date(targetDate)
  }
  if (!isNull(targetDate)) {
    let timestamp = targetDate.getTime()
    let curTimeStamp = new Date().getTime()
    if (!forceFullTime && curTimeStamp >= timestamp) {
      let delta = curTimeStamp - timestamp
      if (delta < 60000) {
        // 小于一分钟
        resText = '刚刚'
      } else if (delta < 3600000) {
        // 小于一小时
        resText = Math.floor(delta / 60000) + '分钟前'
      } else if (delta < 86400000) {
        // 小于一天
        resText = Math.floor(delta / 3600000) + '小时前'
      } else if (delta < 86400000 * 7) {
        // 小于7天
        resText = Math.floor(delta / 86400000) + '天前'
      } else {
        // 大于七天
        if (checkSameYear && dayjs(targetDate).isSame(dayjs(), 'year')) {
          // 同一年份不展示年份
          resText = dayjs(targetDate).format('MM-DD HH:mm')
        } else {
          resText = dayjs(targetDate).format('YYYY-MM-DD HH:mm')
        }
      }
    } else {
      // 当前事件小于目标时间
      resText = dayjs(targetDate).format(formatStr)
    }
  }
  return resText
}
/**
 * 处理文件大小显示
 * @param {Number} size 文件大小，单位为B
 * @returns {String} 文件大小展示文案
 */
export function getSizeText(size) {
  if (isNull(size)) {
    return '未知大小'
  } else {
    let size_kb = size / 1024
    if (size_kb > 1024 * 1024) {
      return parseFloat(size_kb / (1024 * 1024)).toFixed(1) + 'GB'
    } else if (size_kb > 1024) {
      return parseFloat(size_kb / 1024).toFixed(1) + 'MB'
    } else {
      return parseFloat(size_kb).toFixed(1) + 'KB'
    }
  }
}
/**
 * 判断扩展名是否为图片
 * @param {String} extName 扩展名
 * @returns {Boolean} 是否为图片
 */
export function checkImageType(extName = '') {
  let res = false
  if (!isNull(extName)) {
    let imgExtArr = ['jpg', 'jpeg', 'png', 'gif']
    res = imgExtArr.indexOf(extName.toLowerCase()) > -1
  }
  return res
}
/**
 * 通过文件名获取扩展名
 * @param {String} filename 文件名
 * @returns {String} 扩展名
 */
export function getExtName(filename = '') {
  return (filename && filename.lastIndexOf('.') > -1 ? filename.substring(filename.lastIndexOf('.') + 1) : '').toLowerCase()
}

/**
 * 通过文件链接获取文档预览链接
 * @param {String} url 文件名
 * @param {String} mimeType mimeType
 * @returns {String} 预览链接
 */
export const getFilePreviewUrl = (() => {
  const thirdPartyPreviewExtNameArr = ['doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx']
  const modalMediaTypeArr = ['video', 'audio']
  return (url = '', mimeType = '') => {
    let previewUrl = ''
    if (url) {
      const extName = getExtName(url)
      if (extName) {
        const nativePreviewExtNameArr = ['pdf']
        const mediaType = mimeType.split('/')[0]
        if (nativePreviewExtNameArr.indexOf(extName) > -1) {
          // 原生预览
          previewUrl = url
        } else if (modalMediaTypeArr.indexOf(mediaType) > -1) {
          // 音视频弹窗预览
          previewUrl = 'show_modal:' + mediaType
        } else {
          // 第三方预览
          if (extName && thirdPartyPreviewExtNameArr.indexOf(extName) > -1) {
            if (url.indexOf('//') == 0) {
              url = 'http:' + url
            }
            // previewUrl = `https://view.xdocin.com/view?src=${encodeURIComponent(url)}` 免费服务已到期，暂时不用
            previewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}` //微软office预览服务
          }
        }
      }
    }
    return previewUrl
  }
})()

/**
 * 复制文本内容
 * @param {String} text 待复制文案
 * @param {Element} container 容器
 * @returns {Promise}
 */
export function copyText(text, container) {
  return new Promise(function (resolve, reject) {
    var fakeElement = document.createElement('button')
    var clipboard = new Clipboard(fakeElement, {
      text: function () {
        return text
      },
      action: function () {
        return 'copy'
      },
      container: typeof container === 'object' ? container : document.body,
    })
    clipboard.on('success', function (e) {
      clipboard.destroy()
      resolve(e)
    })
    clipboard.on('error', function (e) {
      clipboard.destroy()
      reject(e)
    })
    document.body.appendChild(fakeElement)
    fakeElement.click()
    document.body.removeChild(fakeElement)
  })
}

/**
 * 复制html
 * @param {String} html 待复制的html内容
 */
export function copyHtml(html) {
  let success = true
  // 创建一个用于复制的div
  const forExecElement = document.createElement('div')
  // 放到看不到的地方
  forExecElement.style.position = 'absolute'
  forExecElement.style.left = '-10000px'
  forExecElement.style.top = '-10000px'
  // 将待复制的内容填充进div
  forExecElement.innerHTML = html
  document.body.appendChild(forExecElement)
  // Firefox浏览器中需要让div处于可编辑状态才能执行execCommand方法
  forExecElement.contentEditable = true

  // 创建页面选择方法
  const rangeToSelect = document.createRange()
  rangeToSelect.selectNodeContents(forExecElement)
  const selection = window.getSelection()
  // 移除当前已有的选中区域
  selection.removeAllRanges()
  // 选中用于复制的整个div
  selection.addRange(rangeToSelect)

  try {
    success = document.execCommand('copy', false, null)
  } catch (e) {
    success = false
  }

  // 复制完成后移除div
  document.body.removeChild(forExecElement)

  return success
}

/**
 * 获取图片格式
 * @param {String} imgUrl 图片地址，支持base64
 * @returns {String} 图片格式，如jpeg，png，gif
 */
export function getImgType(imgUrl) {
  var resType = ''
  if (imgUrl.indexOf('data:image') == 0) {
    // base64数据
    resType = imgUrl.split(/:|\/|;/)[2]
  } else {
    // url链接
    imgUrl = imgUrl.split('?')[0]
    resType = imgUrl.substring(imgUrl.lastIndexOf('.') + 1)
  }
  resType = resType.toLowerCase().replace('jpeg', 'jpg')
  return resType
}
/**
 * 获取城市数据（闭包化防止重复请求）
 * @param {Boolean} noCache 是否强制不走缓存(默认为false)
 * @returns {Promise}
 */
export const getCityData = (() => {
  let requested = false
  let requesting = false
  let requestedCallbackArr = []
  return (noCache) => {
    return new Promise((resolve) => {
      let localCityData = JSON.parse(localStorage.city_data || '{}')
      if (isNull(localCityData.lists) || noCache || !requested) {
        if (requesting) {
          // 处于请求中时推入回调队列
          requestedCallbackArr.push(resolve)
        } else {
          requesting = true
          getCityVersion().then((version) => {
            version = version || ''
            const resoveFn = () => {
              requestedCallbackArr.forEach((fn) => {
                typeof fn === 'function' && fn(localCityData.lists)
              })
              requestedCallbackArr = []
              requesting = false
              requested = true
              resolve(localCityData.lists)
            }
            if (isNull(localCityData.lists) || (!isNull(version) && version != localCityData.version)) {
              getCityTree({ version }).then((ret) => {
                // 更新本地数据
                localCityData = { version, lists: ret }
                localStorage.city_data = JSON.stringify(localCityData)
                resoveFn()
              })
            } else {
              resoveFn()
            }
          })
        }
      } else {
        // 直接走缓存
        resolve(localCityData.lists)
      }
    })
  }
})()
// 获取城市数据版本
export function getCityVersion() {
  return new Promise((resolve) => {
    getCityVersionApi()
      .then(function (ret) {
        if (ret.code == 0) {
          resolve(ret.data.version)
        } else {
          resolve(false)
        }
      })
      .catch(function () {
        resolve(false)
      })
  })
}
// 转换停运城市数据
export function transformStopCityData(data) {
  let resArr = []
  if (!isNull(data)) {
    const specArr = ['上海', '北京', '重庆', '天津', '台湾', '澳门特别行政区', '香港特别行政区', '海外']
    data.forEach(function (provinceItem) {
      provinceItem.value = provinceItem.value.replace(/[省市]|自治区|回族|壮族|维吾尔/g, '')
      if (!isNull(provinceItem.children)) {
        provinceItem.children = provinceItem.children.map((item) => {
          return { value: item.value }
        })
      }
      let resProvinceItem = { value: provinceItem.value }
      if (specArr.indexOf(provinceItem.value) == -1) {
        resProvinceItem.children = provinceItem.children
      }
      resArr.push(resProvinceItem)
    })
  }
  return resArr
}

/**
 * 获取图片大小
 * @param {String} imgUrl 图片地址，支持base64
 * @returns {Object} {width:图片宽度, height:图片高度}
 */
export function getImageSizeInfo(imgUrl) {
  return new Promise((resolve, reject) => {
    let img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img = null
    }
    img.onerror = () => {
      reject()
      img = null
    }
    img.src = imgUrl
  })
}
/**
 * 判断元素是否在可视区域
 * @param {Element} el 待判断元素
 * @param {Boolean} allowPart 是否允许元素部分在区域内
 * @param {Object} offsetObj 偏移参数 {top:顶部偏移量, bottom:底部偏移量}
 * @returns {Boolean} 是否在可视区域内
 */
export function isElementInViewport(el, allowPart, offsetObj = {}) {
  if (!el) {
    return false
  }
  const rect = el.getBoundingClientRect()
  if (allowPart) {
    // 部分在可视区域
    const elHeight = rect.height
    return (
      (rect.top >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight) - (offsetObj.bottom || 0)) ||
      (rect.top < 0 && rect.top > 0 - elHeight + (offsetObj.top || 0))
    )
  } else {
    // 全部在可视区域
    return (
      rect.top >= 0 + (offsetObj.top || 0) &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) - (offsetObj.bottom || 0) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }
}
// 数字格式化
export function formatNumber(nums) {
  if (nums > 100000000) {
    nums = parseInt(nums)
    nums = (nums / 100000000).toFixed(5) + '亿'
  } else if (nums > 10000) {
    nums = parseInt(nums)
    nums = (nums / 10000).toFixed(3) + '万'
  }
  return nums
}

// 从 admin 中复制的isIdCardNo
export function isIdCardNo(num) {
  num = num.toUpperCase() //身份证号码为15位或者18位，15位时全为数字，18位前17位为数字，最后一位是校验位，可能为数字或字符X。
  if (!/(^\d{15}$)|(^\d{17}([0-9]|X)$)/.test(num)) {
    //alert('输入的身份证号长度不对，或者号码不符合规定！\n15位号码应全为数字，18位号码末位可以为数字或X。');
    //alert('身份证号长度不正确或不符合规定！');
    return false
  }
  //验证前2位，城市符合
  var aCity = {
    11: '北京',
    12: '天津',
    13: '河北',
    14: '山西',
    15: '内蒙古',
    21: '辽宁',
    22: '吉林',
    23: '黑龙江 ',
    31: '上海',
    32: '江苏',
    33: '浙江',
    34: '安徽',
    35: '福建',
    36: '江西',
    37: '山东',
    41: '河南',
    42: '湖北',
    43: '湖南',
    44: '广东',
    45: '广西',
    46: '海南',
    50: '重庆',
    51: '四川',
    52: '贵州',
    53: '云南',
    54: '西藏',
    61: '陕西',
    62: '甘肃',
    63: '青海',
    64: '宁夏',
    65: '新疆',
    71: '台湾',
    81: '香港',
    82: '澳门',
    91: '国外',
  }
  if (aCity[parseInt(num.substr(0, 2))] == null) {
    //alert('身份证号不正确或不符合规定！');
    return false
  }
  //alert('城市:'+aCity[parseInt(num.substr(0,2))]);

  //下面分别分析出生日期和校验位
  var len, re
  len = num.length
  if (len == 15) {
    re = new RegExp(/^(\d{6})(\d{2})(\d{2})(\d{2})(\d{3})$/)
    let arrSplit = num.match(re) //检查生日日期是否正确
    let dtmBirth = new Date('19' + arrSplit[2] + '/' + arrSplit[3] + '/' + arrSplit[4])
    let bGoodDay
    bGoodDay = dtmBirth.getYear() == Number(arrSplit[2]) && dtmBirth.getMonth() + 1 == Number(arrSplit[3]) && dtmBirth.getDate() == Number(arrSplit[4])
    if (!bGoodDay) {
      //alert('身份证号的出生日期不对！');
      return false
    } else {
      //将15位身份证转成18位 //校验位按照ISO 7064:1983.MOD 11-2的规定生成，X可以认为是数字10。
      let arrInt = new Array(7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2)
      let arrCh = new Array('1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2')
      let nTemp = 0,
        i
      num = num.substr(0, 6) + '19' + num.substr(6, num.length - 6)
      for (i = 0; i < 17; i++) {
        nTemp += num.substr(i, 1) * arrInt[i]
      }
      num += arrCh[nTemp % 11]
      return true
    }
  }
  if (len == 18) {
    re = new RegExp(/^(\d{6})(\d{4})(\d{2})(\d{2})(\d{3})([0-9]|X)$/)
    let arrSplit = num.match(re) //检查生日日期是否正确
    let dtmBirth = new Date(arrSplit[2] + '/' + arrSplit[3] + '/' + arrSplit[4])
    let bGoodDay
    bGoodDay = dtmBirth.getFullYear() == Number(arrSplit[2]) && dtmBirth.getMonth() + 1 == Number(arrSplit[3]) && dtmBirth.getDate() == Number(arrSplit[4])
    if (!bGoodDay) {
      //alert(dtmBirth.getYear());
      //alert(arrSplit[2]);
      //alert('身份证号的出生日期不对！');
      return false
    } else {
      //检验18位身份证的校验码是否正确。 //校验位按照ISO 7064:1983.MOD 11-2的规定生成，X可以认为是数字10。
      let valnum
      let arrInt = new Array(7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2)
      let arrCh = new Array('1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2')
      let nTemp = 0,
        i
      for (i = 0; i < 17; i++) {
        nTemp += num.substr(i, 1) * arrInt[i]
      }
      valnum = arrCh[nTemp % 11]
      if (valnum != num.substr(17, 1)) {
        //alert('18位身份证的校验码不正确！应该为：' + valnum);
        //alert('18位身份证号的校验码不正确！');
        return false
      }
      return true
    }
  }
  return false
}

// 从 admin 中复制的isIdCardNo
export function isMobilePhone(pnstr) {
  var pnexp = /^1\d{10}$/
  return pnexp.test(pnstr)
}
// 获取人性化时间欢迎语
export function getHumanizedTimeWelcome() {
  const curHour = dayjs().hour()
  let resWord = ''
  if (curHour >= 0 && curHour < 11) {
    resWord = '早上好'
  } else if (curHour >= 11 && curHour < 13) {
    resWord = '中午好'
  } else if (curHour >= 13 && curHour < 18) {
    resWord = '下午好'
  } else {
    resWord = '晚上好'
  }
  return resWord
}

// 合并黄绿黄 针对考点关联的; 比如 aa #### bb dd, 则返回 ['aa####bb', 'dd']
export function generateSimpleCombineArr(_arr, isNeedReplaceHash) {
  var GREEN_SYMBOL_STR = '####'
  var tempResArr = []
  var tempValue = ''
  for (var tempCurrIndex = 0; tempCurrIndex < _arr.length; tempCurrIndex++) {
    var tempNextIndex = tempCurrIndex + 1
    var tempPrevIndex = tempCurrIndex - 1
    // 如果自身是#### 或者下一个是#### 或者上一个是####, 则证明当前是黄绿黄中的一部分
    if (_arr[tempCurrIndex] == GREEN_SYMBOL_STR || _arr[tempNextIndex] == GREEN_SYMBOL_STR || (tempPrevIndex >= 0 && _arr[tempPrevIndex] == GREEN_SYMBOL_STR)) {
      // 如果当前是黄并且上一个也是黄并且正处于连续, 得判断下之前是否存在一个黄绿黄; 例子: 黄绿黄黄绿黄; 如果是这样, 就要分开了
      if (_arr[tempCurrIndex] != GREEN_SYMBOL_STR && tempPrevIndex >= 0 && _arr[tempPrevIndex] != GREEN_SYMBOL_STR && tempValue != '') {
        tempResArr.push(tempValue)
        tempValue = ''
      }
      tempValue += _arr[tempCurrIndex]
    } else {
      // 先判断 tempObj是否存在有效值, 如果有的话, 则把它存储起来, 然后重置
      if (tempValue != '') {
        tempResArr.push(tempValue)
        tempValue = ''
      }
      tempResArr.push(_arr[tempCurrIndex])
    }
  }
  // 由于有可能最后一个完整的高亮文本为黄绿黄, 所以需要在for循环结束后再判断一次
  if (tempValue != '') {
    tempResArr.push(tempValue)
  }
  if (isNeedReplaceHash) {
    tempResArr.forEach((tempItem, tempIndex) => {
      tempResArr[tempIndex] = tempItem.replace(/####/gi, '')
    })
  }
  return tempResArr
}

// 由于admin中对于内容作了部分处理, 所以该函数完全借鉴于 layim.js中的layui.data.content
export function chatHistoryReplace(content) {
  let html = function (end) {
    return new RegExp('\\n*\\[' + (end || '') + '(pre|div|p|table|thead|th|tbody|tr|td|ul|li|ol|li|dl|dt|dd|h2|h3|h4|h5)([\\s\\S]*?)\\]\\n*', 'g')
  }

  content = (content || '')
    .replace(/&(?!#?[a-zA-Z0-9]+;)/g, '&amp;')

    // .replace(/</g, '&lt;')
    // .replace(/>/g, '&gt;')
    // .replace(/'/g, '&#39;')
    .replace(/"/g, '&quot;') //XSS

    .replace(/@(\S+)(\s+?|$)/g, '@<a href="javascript:;">$1</a>$2') //转义@

    // .replace(/\[([^\s\[\]]+?)\]/g, function(face) { //转义表情

    //     var alt = face.replace(/^face/g, '');

    //     return '<img class="layim_face" alt="' + alt + '" title="' + alt + '" src="' + faces[alt] + '">';

    // })
    // 评价管理咨询列表中 不要转义表情
    // .replace(/face\[([^\s\[\]]+?)\]/g, function(face) { //转义表情
    //     var alt = face.replace(/^face/g, '');
    //     return '<img class="layim_face" alt="' + alt + '" title="' + alt + '" src="' + faces[alt] + '">';
    // })

    .replace(/img\[([^\s]+?)\]/g, function (img) {
      //转义图片

      return '<img class="layui-layim-photos" src="' + img.replace(/(^img\[)|(\]$)/g, '') + '">'
    })

    .replace(/file\([\s\S]+?\)\[[\s\S]*?\]/g, function (str) {
      //转义文件

      var href = (str.match(/file\(([\s\S]+?)\)\[/) || [])[1]

      var text = (str.match(/\)\[([\s\S]*?)\]/) || [])[1]

      if (!href) return str

      return (
        '<a class="layui-layim-file" href="' + href + '" download target="_blank"><i class="layui-icon">&#xe61e;</i><cite>' + (text || href) + '</cite></a>'
      )
    })

    .replace(/audio\[([^\s]+?)\]/g, function (audio) {
      //转义音频

      return (
        '<div class="layui-unselect layui-layim-audio" layim-event="playAudio" data-src="' +
        audio.replace(/(^audio\[)|(\]$)/g, '') +
        '"><i class="layui-icon">&#xe652;</i><p>音频消息</p></div>'
      )
    })

    .replace(/video\[([^\s]+?)\]/g, function (video) {
      //转义音频

      return (
        '<div class="layui-unselect layui-layim-video" layim-event="playVideo" data-src="' +
        video.replace(/(^video\[)|(\]$)/g, '') +
        '"><i class="layui-icon">&#xe652;</i></div>'
      )
    })

    .replace(/a\([\s\S]+?\)\[[\s\S]*?\]/g, function (str) {
      //转义链接

      var href = (str.match(/a\(([\s\S]+?)\)\[/) || [])[1]

      var text = (str.match(/\)\[([\s\S]*?)\]/) || [])[1]

      if (!href) return str

      return '<a href="' + href + '" target="_blank">' + (text || href) + '</a>'
    })
    .replace(html(), '<$1 $2>')
    .replace(html('/'), '</$1>') //转移HTML代码

    .replace(/\n/g, '<br>') //转义换行
    .replace(/&lt;em&gt;/g, function (item) {
      var str = item.replace(RegExp('&lt;', 'g'), '<')
      str = str.replace(RegExp('&gt;', 'g'), '>')
      return str
    })
    .replace(/&lt;\/em&gt;/g, function (item) {
      var str = item.replace(RegExp('&lt;', 'g'), '<')
      str = str.replace(RegExp('&gt;', 'g'), '>')
      return str
    })
  return content
}
// 获取当前选中内容的html
export function getCopyHtml(selection = window.getSelection()) {
  let html = ''
  if (selection && selection.isCollapsed == false) {
    const rangeObj = selection.getRangeAt(0)
    const docFragment = rangeObj.cloneContents()
    let tempDiv = document.createElement('div')
    tempDiv.appendChild(docFragment)
    html = tempDiv.innerHTML
    tempDiv = null
  }
  return html
}

// 判断两个对象的键值是否相等
export function isObjectValueEqual(a, b) {
  var aProps = Object.getOwnPropertyNames(a)
  var bProps = Object.getOwnPropertyNames(b)
  if (aProps.length != bProps.length) {
    return false
  }
  for (var i = 0; i < aProps.length; i++) {
    var propName = aProps[i]
    if (a[propName] !== b[propName]) {
      return false
    }
  }
  return true
}
// 选择日期组件日期限制
export function datepickerDisabledDate(current) {
  const nextYear = dayjs().year(dayjs().year() + 1)
  return current < dayjs().startOf('day') || current > nextYear.endOf('day')
}
/**
 * 选择日期组件时分限制
 * @param {*} date
 * @param {Number} step 分钟间隔
 * @returns
 */
export function datepickerDisabledTime(date, step) {
  step = step || 1
  let disabledHours = []
  let disabledMinutes = []
  if (date >= dayjs().startOf('day')) {
    const hours = dayjs().hour()
    const minutes = dayjs().minute()
    const isToday = dayjs().isSame(dayjs(date), 'day')
    if (isToday) {
      for (let i = 0; i < hours; i++) {
        disabledHours.push(i)
      }
    }
    if (isToday && date.hour() === hours) {
      for (let i = 0; i < 60; i++) {
        if (i % step !== 0 || i < minutes) {
          disabledMinutes.push(i)
        }
      }
    } else {
      for (let i = 0; i < 60; i++) {
        if (i % step !== 0) {
          disabledMinutes.push(i)
        }
      }
    }
  }
  return {
    disabledHours: () => disabledHours,
    disabledMinutes: () => disabledMinutes,
    disabledSeconds: () => [],
  }
}
// 选择日期组件时间改变事件
export function datepickerChange(dateString, step) {
  step = step || 1
  if (!isNull(dateString) && dayjs(dateString) < dayjs()) {
    let allowMinutes = []
    let returnDate = ''
    const hours = dayjs().hour()
    const minutes = dayjs().minute()
    for (let i = 0; i < 60; i++) {
      if (i % step == 0 && i > minutes) {
        allowMinutes.push(i)
      }
    }
    if (allowMinutes.length > 0) {
      returnDate = dayjs().set({ minutes: allowMinutes[0] }).format('YYYY-MM-DD HH:mm')
    } else {
      returnDate = dayjs()
        .set({ hours: hours + 1, minutes: 0 })
        .format('YYYY-MM-DD HH:mm')
    }
    return { change: true, val: returnDate }
  } else {
    return { change: false, val: dateString }
  }
}

//权限判断
export function hasAuthority(groupIdList) {
  // 1-超管，11-教研组长，13-合作商，24-采购人员, 29-积分商城供应商，具体职位id查看后台-角色管理
  if (store.getters['user/groups']) {
    if (store.getters['user/groups'].indexOf('1') > -1) {
      //需要优先判断供应商身份，因为供应商身份是隐藏某些逻辑
      if (groupIdList.indexOf('29') > -1 && store.getters['user/groups'].indexOf('29') == -1) {
        return false
      } else {
        // 超级管理员
        return true
      }
    } else {
      let has_groups = false
      groupIdList.forEach((group_id) => {
        if (store.getters['user/groups'].indexOf(group_id) > -1) {
          has_groups = true
        }
      })
      return has_groups
    }
  }
}

//处理快递停运模板省份数据
export function handleExpressProvience(province, type) {
  //不传type或type为1用于数据上传，type传2用于数据还原
  if (type == 2) {
    switch (province) {
      case '北京':
        return '北京市'
      case '天津':
        return '天津市'
      case '内蒙古':
        return '内蒙古自治区'
      case '上海':
        return '上海市'
      case '广西':
        return '广西壮族自治区'
      case '重庆':
        return '重庆市'
      case '西藏':
        return '西藏自治区'
      case '宁夏':
        return '宁夏回族自治区'
      case '新疆':
        return '新疆维吾尔自治区'
      case '香港特别行政区':
        return '香港特别行政区'
      case '澳门特别行政区':
        return '澳门特别行政区'
      case '海外':
        return '海外'
      default:
        return province + '省'
    }
  } else {
    return province.replace('省', '').replace('市', '').replace('壮族自治区', '').replace('回族自治区', '').replace('维吾尔自治区', '').replace('自治区', '')
  }
}

// 控制浏览器原生阻止离开事件
export function ctrlOnbeforeunload(state, callback) {
  if (state == true) {
    ctrlOnbeforeunload(false)
    window.onbeforeunload = function (e) {
      typeof callback == 'function' && callback()
      e = e || window.event
      // 兼容IE8和Firefox 4之前的版本
      if (e) {
        e.returnValue = '关闭提示'
      }
      // Chrome, Safari, Firefox 4+, Opera 12+ , IE 9+
      return '关闭提示'
    }
  } else {
    window.onbeforeunload = null
  }
}

// 封装过期控制代码
export function local_set(key, value) {
  var curTime = new Date().getTime() // 当前时间
  localStorage.setItem(key, JSON.stringify({ data: value, time: curTime }))
}
export function local_get(key, exp) {
  var data = localStorage.getItem(key)
  var dataObj = JSON.parse(data)
  if (dataObj == null) {
    return dataObj
  }
  if (exp && new Date().getTime() - (dataObj.time || 0) > exp) {
    console.log('信息已过期')
    return null
  } else {
    var dataObjDatatoJson = dataObj.data
    if (typeof dataObj.data !== 'object') {
      try {
        dataObjDatatoJson = JSON.parse(dataObjDatatoJson)
      } catch (e) {
        // console.log(e)
      }
    }
    return dataObjDatatoJson
  }
}
// localStorage当天有效获取
export function local_today_get(key) {
  var data = localStorage.getItem(key)
  var dataObj = JSON.parse(data)
  if (dataObj == null) {
    // 如果没有则直接返回
    return dataObj
  }
  if (new Date(new Date().setHours(0, 0, 0, 0)) > dataObj.time) {
    // 判断是否是缓存时间是否是今天，不是则返回null
    return null
  } else {
    var dataObjDatatoJson = JSON.parse(dataObj.data)
    return dataObjDatatoJson
  }
}
/**
 * 获取元素总offset，主要针对复杂布局嵌套的情况
 * @param {Element} targetEl 目标元素
 * @param {Element} parentEl 基准父级元素
 * @returns {Number} 总offset
 */
export function getTotalOffset(targetEl, parentEl) {
  let offsetObj = {
    top: 0,
    left: 0,
  }
  if (targetEl) {
    offsetObj.top += targetEl.offsetTop
    offsetObj.left += targetEl.offsetLeft
  }
  if (parentEl) {
    let lastOffsetEl = targetEl
    /* eslint-disable no-constant-condition */
    while (true) {
      lastOffsetEl = lastOffsetEl.offsetParent
      if (!lastOffsetEl || lastOffsetEl === parentEl) {
        break
      }
      if (lastOffsetEl) {
        offsetObj.top += lastOffsetEl.offsetTop
        offsetObj.left += lastOffsetEl.offsetLeft
      }
    }
  }
  return offsetObj
}
/**
 * 将数组中的重复值分组
 * @param {Array} sortData 待分组数组
 * @param {String} sortKey 指定分组依据（即数组中对象的键值，若数组项为字符串则可不传）
 * @returns {Array} 已分组数组
 */
export function sortClass(sortData, sortKey) {
  const groupBy = (array, f) => {
    let groups = {}
    array.forEach((o) => {
      let group = JSON.stringify(f(o))

      groups[group] = groups[group] || []
      groups[group].push(o)
    })
    return Object.keys(groups).map((group) => {
      return groups[group]
    })
  }
  const sorted = groupBy(sortData, (item) => {
    return sortKey ? item[sortKey] : item
  })
  return sorted
}
export function groupByDeveloper(data, groupKey, nameTitle) {
  const groupedData = {}

  data.forEach((item) => {
    // 检查是否存在groupKey和groupKey是否非空
    if (item[groupKey] && item[groupKey].length > 0) {
      item[groupKey].forEach((dev) => {
        const developerName = dev.name || '(空值)' // 如果没有name，归为'(空值)'
        if (!groupedData[developerName]) {
          groupedData[developerName] = []
        }
        groupedData[developerName].push({
          id: item.id,
          ...item,
        })
      })
    } else {
      // 如果不存在groupKey或者为空，归为'(空值)'
      if (!groupedData['(空值)']) {
        groupedData['(空值)'] = []
      }
      groupedData['(空值)'].push({
        id: item.id,
        ...item,
      })
    }
  })

  // 将groupedData对象转换为所需的结构
  return Object.keys(groupedData).map((key) => ({
    id: `group_${nanoid()}`,
    is_group: 1,
    group_name: key === '(空值)' ? '(空值)' : `${nameTitle}：${key}`, //分组表头名称
    children: groupedData[key],
  }))
}
// 判断是否是移动端
export function checkIsMobileDevice() {
  // let res = navigator.userAgent.match(
  //   /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
  // )
  // 设备屏幕宽度判断
  let res = window.screen.width <= 768 || window.screen.height <= 768
  return res
}

// 将html中不属于标签的<和>符号替换为中文符号
export function transformSymbolFromHtml(str) {
  // 定义允许的HTML标签
  let common_tag = [
    'p',
    'br',
    'span',
    'a',
    'img',
    'div',
    'b',
    'i',
    'u',
    'strong',
    'em',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'table',
    'tr',
    'td',
    'th',
    'thead',
    'tbody',
    'tfoot',
    'video',
    'sub',
    'sup',
  ]
  return (
    str
      // 替换所有的"<"和">"
      .replace(/</g, '＜')
      .replace(/>/g, '＞')
      .replace(/＜img([^＞]+?)＞/g, '<img$1>')
      // 将属于HTML标签的"<"和">"替换回来
      .replace(/＜(\/?[a-z!][a-z0-9-]*)([^＜＞]*)＞/g, (match, tagName, attributes) => {
        // 检查是否是有效的HTML标签（允许的标签名和属性）
        if (common_tag.indexOf(tagName.replace('/', '')) > -1 && /^[^＜＞]*$/.test(attributes)) {
          return `<${tagName}${attributes}>`
        }
        return match
      })
  )
}

//将数字转化为中文数字
export function numToChinese(num) {
  const digits = '零一二三四五六七八九'
  const units = ['', '十', '百']

  let result = ''
  let zeroCount = 0
  let unitIndex = 0
  while (num > 0) {
    const digit = num % 10
    num = Math.floor(num / 10)
    const unit = units[unitIndex % 3]
    if (digit === 0) {
      zeroCount++
    } else {
      if (zeroCount > 0) {
        result = digits[0] + result
      }
      zeroCount = 0
      result = digits[digit] + unit + result
    }
    unitIndex++
  }
  if (result === '') {
    result = digits[0]
  }
  result = result.replace('十零', '十').replace('一十', '十')
  return result
}

// 将html内容剥离标签变为纯文本
export function getPureText(text, keepLineBreak) {
  let tempDiv = document.createElement('div')
  //为防止此处赋值html时触发图片加载，将src属性暂时转换
  tempDiv.innerHTML = text.replace(/src=/g, 'src_placeholder=')
  if (keepLineBreak) {
    // 如果需要保留换行必须临时插入到DOM
    Object.assign(tempDiv.style, {
      width: '375px',
      position: 'fixed',
      pointerEvents: 'none',
      opacity: 0,
      top: 0,
      left: 0,
    })
    document.body.appendChild(tempDiv)
  }
  const output = tempDiv.innerText || tempDiv.textContent
  if (keepLineBreak) {
    tempDiv.remove()
  }
  tempDiv = null
  return output
}

// 从列表内获取指定项的名称
export const getItemNameByValue = (list = [], value, valueProp = 'value', nameProp = 'name', childrenProp = 'children') => {
  // 定义递归函数
  const findItemName = (items, topLevelName) => {
    for (const item of items) {
      if (item[valueProp] == value) {
        // 如果当前项的值匹配，返回顶层名称
        return topLevelName || item[nameProp]
      }
      if (item[childrenProp] && item[childrenProp].length > 0) {
        // 如果当前项有子项，递归查找子项，并传递当前项的名称作为顶层名称
        const result = findItemName(item[childrenProp], item[nameProp])
        if (result) {
          // 如果在子项中找到匹配项，返回结果
          return result
        }
      }
    }
    // 如果没有找到匹配项，返回空字符串
    return ''
  }

  // 调用递归函数，初始调用时不传递顶层名称
  return findItemName(list)
}

// 获取多选类型的展示名称
export const getMultiSelectNameList = (dataList, value, valueProp, nameProp) => {
  const valueList = (value || '').split(',')
  const nameArr = valueList.map((item) => getItemNameByValue(dataList, item, valueProp, nameProp)).filter(Boolean)
  return nameArr.join('；')
}

// 判断是否是链接
export function checkIsUrl(string) {
  return /http(s)?:\/\/([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?/.test(string)
}
/**
 * 数字过万用w过千用k，单位可中文（默认w/k）
 * @param {Number} nums
 * @param {Object} extra
 * @param {Object} extra.cn_unit --单位用中文
 * @param {Object} extra.show_plus --数字最后显示‘+’
 * @param {Object} extra.no_thousand --不处理过千
 * @param {Object} extra.no_ten_thousand --不处理过万
 * @returns
 */
export function isOverThousand(nums, extra) {
  nums = parseInt(nums || 0)
  extra = extra || {}
  if (!extra.no_ten_thousand && nums >= 10000) {
    nums = parseFloat(nums / 10000)
    nums = Math.floor(nums * 100) / 100
    if (extra.cn_unit) {
      nums = parseFloat(nums) + '万'
    } else {
      nums = parseFloat(nums) + 'w'
    }
    if (extra.show_plus) {
      nums += '+'
    }
  } else if (!extra.no_thousand && nums >= 1000) {
    nums = parseFloat(nums / 1000)
    nums = Math.floor(nums * 100) / 100
    if (extra.cn_unit) {
      nums = parseFloat(nums) + '千'
    } else {
      nums = parseFloat(nums) + 'k'
    }
    if (extra.show_plus) {
      nums += '+'
    }
  }
  return nums
}
// 将字符串中的url转换为a标签
export function transformUrlFromStr(str) {
  str = str || ''
  var urlPattern = /http(s)?:\/\/([\w-]+\.)+[\w-]+(:\d+)?(\/[^ \t\n\r\f\v]*)?/g
  var replacedText = str.replace(urlPattern, (url) => {
    return '<a href="' + url + '" target="_blank">' + url + '</a>'
  })
  return replacedText
}
// 时间戳格式化
export const formatTimestamp = (timestamp, dateFormat = 'YYYY-MM-DD') => {
  timestamp = parseInt((timestamp + '').padEnd(13, 0))
  const inputDay = dayjs(timestamp)
  return inputDay.format(dateFormat)
}
/**
 * 轮询一定次数，等待在XX条件下再执行XX方法
 * @param {function} func --判断是否达成条件，返回true或false
 * @param {Number} maxTimes --最大执行次数
 * @param {Number} curTimes --当前执行次数
 * @param {Number} delayTime --间隔多少秒执行一次
 * @returns
 */
export function waitingExec(func, maxTimes, curTimes = 0, delayTime) {
  return new Promise(function (resolve, reject) {
    if (maxTimes && curTimes >= maxTimes) {
      return resolve(false)
    }
    if (typeof func === 'function') {
      var res = func()
      if (res) {
        return resolve(res)
      } else {
        setTimeout(function () {
          waitingExec(func, maxTimes, curTimes + 1)
            .then(resolve)
            .catch(reject)
        }, delayTime)
      }
    } else {
      return resolve()
    }
  })
}

//表情库
const faces = (function () {
  var alt = [
      '[微笑]',
      '[撇嘴]',
      '[色]',
      '[发呆]',
      '[得意]',
      '[流泪]',
      '[害羞]',
      '[闭嘴]',
      '[睡]',
      '[大哭]',
      '[尴尬]',
      '[发怒]',
      '[调皮]',
      '[呲牙]',
      '[惊讶]',
      '[难过]',
      '[酷]',
      '[冷汗]',
      '[抓狂]',
      '[吐]',
      '[偷笑]',
      '[愉快]',
      '[白眼]',
      '[傲慢]',
      '[感冒]',
      '[困]',
      '[惊恐]',
      '[流汗]',
      '[憨笑]',
      '[悠闲]',
      '[奋斗]',
      '[咒骂]',
      '[疑问]',
      '[嘘]',
      '[晕]',
      '[笑哭]',
      '[衰]',
      '[骷髅]',
      '[敲打]',
      '[再见]',
      '[擦汗]',
      '[抠鼻]',
      '[鼓掌]',
      '[糗大了]',
      '[坏笑]',
      '[左哼哼]',
      '[右哼哼]',
      '[哈欠]',
      '[鄙视]',
      '[委屈]',
      '[快哭了]',
      '[阴险]',
      '[亲亲]',
      '[吓]',
      '[可怜]',
      '[菜刀]',
      '[西瓜]',
      '[啤酒]',
      '[咖啡]',
      '[猪头]',
      '[玫瑰]',
      '[凋谢]',
      '[嘴唇]',
      '[爱心]',
      '[心碎]',
      '[蛋糕]',
      '[炸弹]',
      '[便便]',
      '[月亮]',
      '[太阳]',
      '[礼物]',
      '[拥抱]',
      '[强]',
      '[弱]',
      '[握手]',
      '[胜利]',
      '[抱拳]',
      '[勾引]',
      '[拳头]',
      '[OK]',
      '[跳跳]',
      '[发抖]',
      '[怄火]',
      '[转圈]',
      '[惊慌]',
      '[失望]',
      '[无语]',
      '[嘿哈]',
      '[捂脸]',
      '[奸笑]',
      '[机智]',
      '[皱眉]',
      '[耶]',
      '[鬼]',
      '[合十]',
      '[强壮]',
      '[礼花]',
      '[红包]',
      '[发财]',
      '[福]',
    ],
    arr = {}
  alt.forEach(function (item, index) {
    arr[item] = '/static/images/faces/' + index + '.png'
  })
  return arr
})()
//转义文本表情
export const transformFaceText = (content = '') => {
  //eslint-disable-next-line
  return content.replace(/\[([^\s\[\]]+?)\]/g, function (face) {
    var alt = face
    return faces[alt] ? '<img class="face-text" alt="' + alt + '" title="' + alt + '" src="' + faces[alt] + '">' : alt
  })
}
//计算树形结构深度
export const getListDeep = (list = []) => {
  // 如果列表为空或者不是数组，直接返回0
  if (!Array.isArray(list) || list.length === 0) {
    return 0
  }
  // 递归函数来计算单个节点的深度
  function getNodeDepth(node, currentDepth) {
    if (!node.children || node.children.length === 0) {
      // 如果没有子节点，返回当前深度
      return currentDepth
    }
    // 否则，递归计算每个子节点的深度
    return Math.max(...node.children.map((child) => getNodeDepth(child, currentDepth + 1)))
  }

  // 从根节点开始计算深度，初始深度为1
  return Math.max(...list.map((node) => getNodeDepth(node, 1)))
}
//获取两组时间的占比
export function calculateTimeIntervalRatio(startEnd1, startEnd2) {
  // 将标准时间格式字符串转换为时间戳
  function convertToTimestamp(timeStr) {
    return new Date(timeStr).getTime()
  }

  // 计算第一组时间间隔
  const interval1 = convertToTimestamp(startEnd1[1]) - convertToTimestamp(startEnd1[0])
  // 计算第二组时间间隔
  const interval2 = convertToTimestamp(startEnd2[1]) - convertToTimestamp(startEnd2[0])

  // 确定哪个时间间隔更大作为分母
  const numerator = Math.min(interval1, interval2)
  const denominator = Math.max(interval1, interval2)

  // 计算比例值
  const ratio = numerator / denominator

  return ratio
}
export function findLatestTimeArrayIndex(arr) {
  // 将标准时间格式字符串转换为时间戳
  function convertToTimestamp(timeStr) {
    return new Date(timeStr).getTime()
  }

  // 初始化变量以存储最晚的时间戳和对应的下标
  let latestTimestamp = 0
  let latestIndex = -1

  // 遍历数组以找到最晚的时间
  for (let i = 0; i < arr.length; i++) {
    const endTime = convertToTimestamp(arr[i][1])

    if (endTime > latestTimestamp) {
      latestTimestamp = endTime
      latestIndex = i
    }
  }

  return latestIndex
}
export function findEarliestTimeArrayIndex(arr) {
  // 将标准时间格式字符串转换为时间戳
  function convertToTimestamp(timeStr) {
    return new Date(timeStr).getTime()
  }

  // 初始化变量以存储最早的时间戳和对应的下标
  let earliestTimestamp = Infinity
  let earliestIndex = -1

  // 遍历数组以找到最早的时间
  for (let i = 0; i < arr.length; i++) {
    const startTime = convertToTimestamp(arr[i][0])
    if (startTime < earliestTimestamp) {
      earliestTimestamp = startTime
      earliestIndex = i
    }
  }

  return earliestIndex
}
//去除对象中不要的属性
export function removeUnwantedProperties(obj, unwantedProps) {
  // 使用Object.keys和reduce方法遍历对象的属性
  return Object.keys(obj).reduce((newObj, key) => {
    // 检查当前属性是否不在不需要的属性数组中
    if (!unwantedProps.includes(key)) {
      // 如果不在数组中，则将属性添加到新对象中
      newObj[key] = obj[key]
    }
    return newObj
  }, {})
}
//重置变量
export function initializeExceptCertainProperties(obj, filterFields, exceptions) {
  // 获取对象所有键
  const keys = Object.keys(obj)
  // 过滤掉需要保留的键（即filterFields中的键和exceptions中的键）
  const keysToInitialize = keys.filter((key) => !filterFields.includes(key) && !exceptions.includes(key))

  // 遍历需要初始化的键
  keysToInitialize.forEach((key) => {
    // 如果属性值是数组，则初始化为空数组，否则初始化为null
    if (Array.isArray(obj[key])) {
      obj[key] = []
    } else {
      obj[key] = null
    }
  })

  return obj
}
export function disableFields(arr, filterFields) {
  // 将filterFields中的key提取出来，方便后续查找
  const keysToDisable = filterFields.map((field) => field.key)

  // 遍历arr数组，检查并更新disabled状态
  arr.forEach((item) => {
    // 如果item的value在keysToDisable中，则disabled设置为true，否则设置为false
    item.disabled = keysToDisable.includes(item.value)
  })
  return arr
}
//判断对象数组是否一致
export function isObjectArrayEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false
  }

  // 用于检查循环引用
  const seen = new WeakMap()

  function isObjectEqual(obj1, obj2, seen) {
    if (obj1 === obj2) {
      return true
    }

    if (
      typeof obj1 === 'function' ||
      obj1 === undefined ||
      obj2 === undefined ||
      obj1 instanceof Date !== obj2 instanceof Date ||
      (Array.isArray(obj1) && !Array.isArray(obj2)) ||
      (!Array.isArray(obj1) && Array.isArray(obj2))
    ) {
      return false
    }

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) {
        return false
      }
      for (let i = 0; i < obj1.length; i++) {
        if (!isObjectEqual(obj1[i], obj2[i], seen)) {
          return false
        }
      }
      return true
    }

    if (isObject(obj1) && isObject(obj2)) {
      if (seen.has(obj1) && seen.get(obj1) === obj2) {
        return true
      }
      seen.set(obj1, obj2)

      const keys1 = Object.keys(obj1).sort()
      const keys2 = Object.keys(obj2).sort()

      if (keys1.length !== keys2.length) {
        return false
      }

      for (const key of keys1) {
        if (!keys2.includes(key) || !isObjectEqual(obj1[key], obj2[key], seen)) {
          return false
        }
      }
      return true
    }

    return obj1 === obj2
  }

  function isObject(item) {
    return item !== null && typeof item === 'object'
  }

  // 比较数组中的每个对象
  for (let i = 0; i < arr1.length; i++) {
    if (!isObjectEqual(arr1[i], arr2[i], seen)) {
      return false
    }
  }

  return true
}

/**
 * 从字符串中移除 HTML 标签，同时允许指定的标签保留，并忽略特定标签。
 *
 * @param {string} str - 包含 HTML 的输入字符串。
 * @param {string[]} allowTags - 允许保留的标签数组（如 ['b', 'i']）。
 * @param {string[]} nodoTags - 完全忽略的标签数组（如 ['script', 'style']）。
 * @param {boolean} isImg - 是否允许 <img> 标签（如果它在 allowTags 中）。默认为 true。
 * @returns {string} 处理后的字符串，保留或移除指定的标签。
 */
export function stripHtmlTags(str, allowTags = [], nodoTags = [], isImg = true) {
  // 使用正则表达式提取输入字符串中的所有 HTML 标签
  const tagRegex = /<([/!?]?[a-z][a-z0-9-]*)[^<>]*>/gi
  let match
  const tags = []

  // 通过正则表达式循环匹配所有的 HTML 标签，并存储到 tags 数组中
  while ((match = tagRegex.exec(str)) !== null) {
    tags.push(match)
  }

  // 遍历找到的每一个 HTML 标签
  tags.forEach((tagMatch) => {
    const fullTag = tagMatch[0] // 完整的 HTML 标签（如 '<b>'）
    const tagName = tagMatch[1].replace('/', '').toLowerCase() // 提取标签名（如 'b'）

    // 如果标签名在 nodoTags 数组中，则跳过处理
    if (nodoTags.includes(tagName)) {
      return
    }

    // 如果标签名在 allowTags 数组中
    if (allowTags.includes(tagName)) {
      // 特殊处理 <img> 标签：只有当 isImg 为 true 时才保留
      if (tagName === 'img' && isImg) {
        return // 保留 <img> 标签
      }

      // 重建标签，保持原始结构（转为小写）
      str = str.replace(fullTag, `<${tagMatch[1].toLowerCase()}>`)
    } else {
      // 如果标签不在允许列表中，则从字符串中移除
      str = str.replace(fullTag, '')
    }
  })

  return str // 返回处理后的字符串
}
//判断两个对象是否一致
export function isDeepEqual(obj1, obj2) {
  // 获取两个对象的类型
  const type1 = typeof obj1
  const type2 = typeof obj2

  // 如果两个对象的类型不同，则它们不相等
  if (type1 !== type2) return false

  // 如果两个对象都是基本类型，则直接比较它们的值
  if (type1 !== 'object' || obj1 === null || obj2 === null) {
    return obj1 === obj2
  }

  // 处理数组的情况
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false
    for (let i = 0; i < obj1.length; i++) {
      if (!isDeepEqual(obj1[i], obj2[i])) {
        return false
      }
    }
    return true
  }

  // 获取两个对象的属性列表
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  // 如果属性数量不同，则两个对象不相等
  if (keys1.length !== keys2.length) return false

  // 检查每个属性
  for (let key of keys1) {
    if (!keys2.includes(key) || !isDeepEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  // 如果所有属性都相等，则两个对象相等
  return true
}
export function assignKeyIdIfMissing(item) {
  if (!item.key) {
    item.key = item.id // 如果没有key，则将key设置为id
  }
  if (item.children) {
    // 如果有子项，递归调用这个函数
    item.children.forEach((child) => assignKeyIdIfMissing(child))
  }
}
export function containsRPrefix(array) {
  return array.some((item) => {
    // 检查项是否为字符串并且以'r_'开头
    return typeof item === 'string' && item.startsWith('r_')
  })
}
export function separateArray(arr) {
  let startsWithR = [] // 去除'r_'前缀的数组
  let notStartsWithR = [] // 不以'r_'开头的数组

  arr.forEach((item) => {
    if (typeof item === 'string' && item.startsWith('r_')) {
      // 使用正则替换'r_'前缀并添加到startsWithR数组
      startsWithR.push(item.replace(/^r_/, ''))
    } else {
      // 不以'r_'开头的元素直接添加到notStartsWithR数组
      notStartsWithR.push(item)
    }
  })
  return [startsWithR, notStartsWithR]
}
export function findAndUpdateItem(items, id, updateObj) {
  let found = false
  for (let i = 0; i < items.length; i++) {
    if (items[i].id === id) {
      // 找到匹配的id，更新该项
      items[i] = { ...items[i], ...updateObj }
      found = true
      break // 如果只需要找到第一个匹配项，找到后就可以停止搜索
    } else if (Array.isArray(items[i].children)) {
      // 如果当前项有children属性且为数组，则递归搜索
      found = findAndUpdateItem(items[i].children, id, updateObj) || found
    }
  }
  return found
}
export function convertToFriendlyFormat(dateStr) {
  // 如果输入为空，直接返回原值
  if (!dateStr) {
    return dateStr
  }

  // 将输入的日期时间字符串转换为 Date 对象
  const dt = new Date(dateStr)

  // 检查日期是否有效
  if (isNaN(dt.getTime())) {
    return dateStr // 如果转换失败，返回原字符串
  }

  // 获取当前日期和时间
  const today = new Date()
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  // 计算目标日期与今天的天数差
  const diffDays = Math.floor((dt - normalizedToday) / (1000 * 60 * 60 * 24))

  // 辅助函数：获取星期名称
  function getWeekdayName(day) {
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
    return weekdays[day]
  }

  // 辅助函数：格式化时间
  function formatTime(date) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }

  // 获取目标日期的星期几
  const targetWeekday = dt.getDay()

  // 获取今天是周几（0 表示周日）
  const todayWeekday = today.getDay()

  // 处理周日特殊情况：周日算作本周的最后一天
  const adjustedTodayWeekday = todayWeekday === 0 ? 7 : todayWeekday // 将周日视为第 7 天

  // 如果目标日期在本周内
  if (diffDays >= -adjustedTodayWeekday && diffDays <= 7 - adjustedTodayWeekday) {
    return `本${getWeekdayName(targetWeekday)} ${formatTime(dt)}`
  }

  // 如果目标日期在下周内
  if (diffDays >= 7 - adjustedTodayWeekday && diffDays <= 14 - adjustedTodayWeekday) {
    return `下${getWeekdayName(targetWeekday)} ${formatTime(dt)}`
  }
  // 获取当前日期

  const currentYear = today.getFullYear()

  // 将输入的时间字符串转换为Date对象
  const inputDate = new Date(dateStr)

  // 检查输入时间是否是本年
  if (inputDate.getFullYear() === currentYear) {
    // 如果是本年，格式化为"MM-DD HH:mm"格式
    const month = String(inputDate.getMonth() + 1).padStart(2, '0') // 月份从0开始，需要加1
    const day = String(inputDate.getDate()).padStart(2, '0')
    const hours = String(inputDate.getHours()).padStart(2, '0')
    const minutes = String(inputDate.getMinutes()).padStart(2, '0')

    return `${month}-${day} ${hours}:${minutes}`
  } else {
    return dateStr
  }
}
export function includesAny(array, values) {
  // 将 array 中的所有元素转换为字符串
  const stringArray = array.map((item) => String(item))
  // 将 values 中的所有元素转换为字符串
  const stringValues = values.map((value) => String(value))
  // 使用 some 方法检查 stringValues 中的任意值是否在 stringArray 中
  return stringValues.some((value) => stringArray.includes(value))
}
export function getMonthStartAndEnd(monthYear) {
  // 将输入的字符串分割为年和月
  const [year, month] = monthYear.split('-').map(Number)

  // 创建一个日期对象，表示该月的第一天
  const start = new Date(year, month - 1, 1)

  // 计算下个月的第一天，然后减去一天，得到这个月的最后一天
  const end = new Date(year, month, 0)

  // 格式化日期为YYYY-MM-DD格式
  const formatDate = (date) => {
    const d = new Date(date)
    let month = '' + (d.getMonth() + 1)
    let day = '' + d.getDate()
    let year = d.getFullYear()

    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day

    return [year, month, day].join('-')
  }

  // 返回格式化后的开始和结束日期
  return {
    start: formatDate(start),
    end: formatDate(end),
  }
}
//抽取名称
export function extractNames(data) {
  let names = []

  // 遍历数据
  data.forEach((item) => {
    // 如果当前对象有 name 字段并且不是部门（selectable 为 true 或者没有该字段），则添加到结果数组中
    if (item.name && (item.selectable === undefined || item.selectable === true)) {
      names.push(item.name)
    }

    // 如果当前对象有 children，则递归处理 children 中的项
    if (item.children && Array.isArray(item.children)) {
      names = names.concat(extractNames(item.children))
    }
  })

  return names
}
export function boldNamesInText(text, names) {
  // 1. 处理日期（2025-01-17, 2025/01/17, 2025.01.17 格式）
  const dateRegex = /(\b(?:\d{4}[-/.年]\d{1,2}[-/.月]\d{1,2}(日)?\s*|\d{1,2}[-/.月]\d{1,2}(日)?\s*))/g
  text = text.replace(dateRegex, (match) => {
    // 直接返回整个匹配结果，并将其加粗
    return `<span style="font-weight:bold">${match}</span>`
  })

  // 2. 处理时间（10:30, 16:30 格式）
  const timeRegex = /\b\d{1,2}:\d{2}\b/g
  text = text.replace(timeRegex, '<span style="font-weight:bold">$&</span>')

  // 3. 处理“每天”和“每周”
  text = text.replaceAll('每天', '<span style="font-weight:bold">每天</span>')
  text = text.replaceAll('每周', '<span style="font-weight:bold">每周</span>')

  // 4. 处理人名：遍历所有名字进行加粗
  names.forEach((name) => {
    // 创建正则表达式以匹配完整的人名
    const regex = new RegExp(`(${name})`, 'g')
    // 将匹配到的人名用 <span style="font-weight:bold"> 标签包裹
    text = text.replace(regex, '<span style="font-weight:bold">$1</span>')
  })

  // 5. 处理数字：排除已加粗的数字部分
  const numberRegex = /\b[+-]?\d{1,3}(?:,\d{3})*(?:\.\d+)?\b/g
  // 先匹配加粗的数字，确保不会重复加粗
  const alreadyBoldedNumbers = /<span style="font-weight:bold">(\d+)<\/span>/g

  // 对未加粗的数字部分进行加粗
  text = text.replace(numberRegex, (match) => {
    // 检查数字是否已经被加粗
    if (!alreadyBoldedNumbers.test(match)) {
      return `<span style="font-weight:bold">${match}</span>`
    }
    return match
  })

  return text
}
export function getNextVersion(data) {
  // 定义一个函数用于解析版本号并比较大小
  function parseVersion(version) {
    // 使用正则表达式匹配版本号，例如 "v1.0" 或 "V1.0"
    const match = version.match(/([vV]?)(\d+)\.(\d+)/)
    if (!match) return null // 如果无法匹配版本号，返回 null

    // 提取版本号的主版本和次版本
    const majorVersion = parseInt(match[2], 10)
    const minorVersion = parseInt(match[3], 10)

    return { majorVersion, minorVersion }
  }

  // 定义一个函数用于比较版本号大小
  function compareVersions(versionA, versionB) {
    if (versionA.majorVersion > versionB.majorVersion) return 1
    if (versionA.majorVersion < versionB.majorVersion) return -1
    if (versionA.minorVersion > versionB.minorVersion) return 1
    if (versionA.minorVersion < versionB.minorVersion) return -1
    return 0
  }

  // 遍历数组，找到最高的版本号
  let highestVersion = { majorVersion: 1, minorVersion: 0 } // 默认版本号 v1.0
  let highestVersionString = 'V1.0' // 默认版本号字符串
  let hasValidVersion = false // 标记是否找到有效的版本号

  for (const item of data) {
    const parsedVersion = parseVersion(item.title)
    if (parsedVersion) {
      hasValidVersion = true
      if (compareVersions(parsedVersion, highestVersion) > 0) {
        highestVersion = parsedVersion
        highestVersionString = item.title
      }
    }
  }

  // 如果没有找到有效的版本号，返回默认值 v1.0
  if (!hasValidVersion) return 'V1.0'

  // 对最高版本号进行自增
  const [prefix, major, minor] = highestVersionString.match(/([vV]?)(\d+)\.(\d+)/).slice(1)
  const nextVersion = `${prefix || 'V'}${major}.${parseInt(minor, 10) + 1}`

  return nextVersion
}
let seenIds = []
export function flattenData(data, isFirst = false) {
  let flatData = []

  // 用于去重的 Set 存储已经出现的 id
  if (isFirst) {
    seenIds = []
  }

  data.forEach((item) => {
    // 只处理非部门（selectable: false）的数据

    // 如果 id 不在 seenIds 中，说明是第一次出现，进行处理
    if (!seenIds.includes(item.id) && item.selectable !== false) {
      // 添加当前项的扁平化数据
      flatData.push(item)
      seenIds.push(item.id) // 标记该 id 已处理
    }

    // 如果有子元素，则递归地处理子元素
    if (item.children && item.children.length > 0) {
      flatData = flatData.concat(flattenData(item.children)) // 递归调用
    }
  })

  return flatData
}
export function isJSONString(str) {
  try {
    // 尝试解析字符串为 JSON 对象
    JSON.parse(str)
    // 如果解析成功，返回 true
    return true
  } catch (e) {
    // 如果解析失败，捕获异常并返回 false
    return false
  }
}
export function filterSchemeData(data) {
  // 定义一个函数，用于判断 children 中的数据是否为空
  function isChildrenEmpty(children) {
    return children.every((item) => {
      return (
        item.routePath === '' &&
        (item.operate === null || item.operate === '' || item.operate === undefined) &&
        item.componentsName === '' &&
        item.handleEvent === ''
      )
    })
  }

  // 使用 filter 方法过滤掉 children 中数据为空的对象
  return data.filter((item) => !isChildrenEmpty(item.children))
}
export function findFirstEmptyItem(data) {
  // 遍历整个数据结构
  for (let parentIndex = 0; parentIndex < data.length; parentIndex++) {
    const children = data[parentIndex].children

    // 检查每个子项
    for (let childIndex = 0; childIndex < children.length; childIndex++) {
      const child = children[childIndex]

      // 检查是否所有值都为空（跳过完全为空的项）
      const allValuesEmpty =
        (!child.routePath || child.routePath.trim() === '') &&
        (child.operate === null || child.operate === undefined) &&
        (!child.componentsName || child.componentsName.trim() === '') &&
        (!child.handleEvent || child.handleEvent.trim() === '')

      // 如果所有值都为空，则跳过当前项
      if (allValuesEmpty) {
        continue
      }

      // 检查子项的属性是否为空（至少有一个值不为空时才进行判断）
      if (
        !child.routePath ||
        child.routePath.trim() === '' ||
        child.operate === null ||
        child.operate === undefined ||
        !child.componentsName ||
        child.componentsName.trim() === '' ||
        !child.handleEvent ||
        child.handleEvent.trim() === ''
      ) {
        // 如果找到第一个空项，立即返回父下标和子下标
        return { parentIndex, childIndex }
      }
    }
  }

  // 如果没有找到空项，返回 null
  return null
}
export function migrateItems(sourceArray, targetArray, userId) {
  // 创建一个新数组，用于存储满足条件的元素
  const itemsToMigrate = []

  // 遍历源数组，找出满足条件的元素
  for (let i = 0; i < sourceArray.length; i++) {
    if (sourceArray[i].id === userId) {
      // 如果满足条件，将该元素从源数组中移除，并添加到目标数组中
      itemsToMigrate.push(sourceArray.splice(i, 1)[0])
      i-- // 因为移除了一个元素，所以需要调整索引
    }
  }

  // 将满足条件的元素添加到目标数组
  targetArray.push(...itemsToMigrate)

  return {
    updatedSourceArray: sourceArray,
    updatedTargetArray: targetArray,
  }
}
export function getFilePages(file, type, record) {
  return new Promise((resolve) => {
    if (type === 'pdf') {
      const fileReader = new FileReader()
      fileReader.onload = async (event) => {
        const pdfData = event.target.result

        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://kanban.ehafo.com/article_attachment/2876_1739012243430_0/pdf.worker.mjs'
        const loadingTask = pdfjsLib.getDocument({ data: pdfData })
        const pdf = await loadingTask.promise

        record.pages = pdf._pdfInfo.numPages
        resolve(record.pages)
      }
      fileReader.readAsArrayBuffer(file)
    } else if (type === 'pptx') {
      const fileReader = new FileReader()
      fileReader.onload = async (event) => {
        const zip = await JSZip.loadAsync(event.target.result)

        const slidesFolder = zip.folder('ppt/slides')
        if (slidesFolder) {
          const slideFiles = Object.values(slidesFolder.files).filter((file) => {
            return file.name.includes('ppt/slides/slide')
          })

          record.pages = slideFiles.length
        } else {
          record.pages = 0
        }

        resolve(record.pages)
      }
      fileReader.readAsArrayBuffer(file)
    } else if (type == 'docx') {
      const fileReader = new FileReader()
      fileReader.onload = async (event) => {
        const arrayBuffer = event.target.result

        // 使用 mammoth 提取文本
        const result = await mammoth.extractRawText({ arrayBuffer })
        const text = result.value || ''

        // 去除所有空白字符
        const cleanText = text.replace(/\s+/g, '')
        console.log(cleanText)
        // 计算字数
        const wordCount = cleanText.length
        record.wordCount = wordCount
        resolve(record.wordCount)
      }
      fileReader.readAsArrayBuffer(file)
    }
  })
}
export function getWeekRange() {
  const today = new Date()
  const dayOfWeek = today.getDay() // 当前是周几（0 表示周日，1 表示周一，以此类推）
  const firstDayOfWeek = new Date(today) // 本周第一天（周一）
  firstDayOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1))

  // 本周的日期范围
  const startDateThisWeek = new Date(firstDayOfWeek)
  const endDateThisWeek = new Date(firstDayOfWeek)
  endDateThisWeek.setDate(firstDayOfWeek.getDate() + 6)

  // 下周的日期范围
  const startDateNextWeek = new Date(firstDayOfWeek)
  startDateNextWeek.setDate(firstDayOfWeek.getDate() + 7)
  const endDateNextWeek = new Date(firstDayOfWeek)
  endDateNextWeek.setDate(firstDayOfWeek.getDate() + 13)

  // 格式化日期为 MM.DD 格式
  const formatDate = (date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${month}.${day}`
  }

  // 生成本周和下周的日期范围字符串
  const thisWeekRange = `${formatDate(startDateThisWeek)}-${formatDate(endDateThisWeek)}`
  const nextWeekRange = `${formatDate(startDateNextWeek)}-${formatDate(endDateNextWeek)}`

  return { thisWeekRange, nextWeekRange }
}
